import { useState } from 'react';
import { Settings } from 'lucide-react';
import SortMenu from './SortMenu';
import CurrencyMenu from './CurrencyMenu';
import FilterMenu from './FilterMenu';

/**
 * AdvancedSearchOptions Component
 * 
 * Provides advanced search functionality including sorting options that match the backend logic.
 * 
 * Usage Example:
 * ```jsx
 * const [searchResults, setSearchResults] = useState([]);
 * const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
 * 
 * const handleSortChange = (sortOption, applySortingFn) => {
 *   const sortedResults = applySortingFn(searchResults, sortOption);
 *   setSearchResults(sortedResults);
 * };
 * 
 * const advancedOptions = AdvancedSearchOptions({
 *   showAdvancedOptions,
 *   toggleAdvancedOptions: () => setShowAdvancedOptions(!showAdvancedOptions),
 *   onSortChange: handleSortChange
 * });
 * 
 * // In your JSX:
 * <div className="search-container">
 *   <div className="search-bar">
 *     <input type="search" />
 *     {advancedOptions.renderToggleButton()}
 *   </div>
 *   {advancedOptions.renderDropdownMenu()}
 * </div>
 * 
 * // To manually sort results:
 * const sortedResults = advancedOptions.applySorting(results, 'price');
 * ```
 * 
 * Sorting Options:
 * - amazon_results: Default backend logic (position weight + locale count)
 * - price: Sort by lowest price first
 * - amazon: Sort by Amazon's original order (position weight only)
 * - results: Sort by number of available domains/locales
 */
function AdvancedSearchOptions({ showAdvancedOptions, toggleAdvancedOptions, onSortChange, onCurrencyChange, onFilterChange, activeFilters, availableFilterOptions }) {
  // Method to render the toggle button (for inside search bar)
  const renderToggleButton = () => (
    <button
      type="button"
      className="px-4 focus:outline-none hover:text-gray-800 transition-colors"
      aria-label="Advanced Search Options"
      onClick={toggleAdvancedOptions}
      title="אפשרויות חיפוש מתקדמות"
    >
      <Settings className="w-5 h-5 text-gray-500 hover:text-gray-700 transition-colors" />
    </button>
  );

  // Method to render the dropdown menu (for below search bar)
  const renderDropdownMenu = () => (
    <div className={`max-w-3xl mx-auto mt-2 overflow-hidden transition-all duration-300 ease-in-out ${showAdvancedOptions ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0'}`}>
      <div className="flex justify-start items-center gap-8 py-2 relative" dir="rtl">
        {/* Sort */}
        <SortMenu onSortChange={onSortChange} />

        {/* Filter */}
        <FilterMenu 
          onFilterChange={onFilterChange}
          filters={activeFilters}
          availableOptions={availableFilterOptions}
        />

        {/* Currency */}
        <CurrencyMenu onCurrencyChange={onCurrencyChange} />
      </div>
    </div>
  );

  // Return object with render methods
  return {
    renderToggleButton,
    renderDropdownMenu
  };
}

export default AdvancedSearchOptions; 